// Department Distribution Component for Dashboard

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDepartmentStats, useDashboardStats } from '../../stores/dashboardStore';
import { useWorkshopStore } from '../../stores/workshopStore';

interface DepartmentCardProps {
  name: string;
  code: string;
  workshopCount: number;
  completionRate: number;
  completedCount: number;
  lateCount: number;
  estimatedSavings: number;
  releasedSavings: number;
  color: string;
  onClick?: () => void;
}

const DepartmentCard: React.FC<DepartmentCardProps> = ({ 
  name, 
  code, 
  workshopCount, 
  completionRate,
  completedCount,
  lateCount,
  estimatedSavings,
  releasedSavings,
  color,
  onClick
}) => {
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M€`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K€`;
    } else {
      return `${Math.round(value)}€`;
    }
  };

  return (
    <div 
      className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200/50 shadow-xl hover:shadow-2xl transform hover:-translate-y-3 hover:rotate-1 transition-all duration-500 cursor-pointer group backdrop-blur-sm"
      onClick={onClick}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-30 group-hover:scale-150 transition-transform duration-700" style={{ backgroundColor: color }}></div>
        <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full opacity-20 group-hover:scale-125 transition-transform duration-700 delay-150" style={{ backgroundColor: color }}></div>
      </div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <h3 className="text-xl font-black text-gray-900 group-hover:text-gray-800 transition-colors duration-200 tracking-tight">{name}</h3>
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">{code}</p>
          </div>
          <div className="relative">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300 relative overflow-hidden"
              style={{ backgroundColor: color }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
              <span className="relative z-10">{workshopCount}</span>
            </div>
          </div>
        </div>
      
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700">Completion Rate</span>
            <span className="text-lg font-black text-gray-900">{completionRate}%</span>
          </div>
          
          <div className="relative">
            <div className="w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-3 shadow-inner">
              <div 
                className="h-3 rounded-full transition-all duration-700 group-hover:shadow-lg relative overflow-hidden"
                style={{ 
                  width: `${Math.min(completionRate, 100)}%`,
                  background: `linear-gradient(90deg, ${color}CC, ${color})`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"></div>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200/70">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-2 rounded-xl bg-green-50/80 border border-green-100 group-hover:bg-green-100/80 transition-colors duration-300">
                <div className="font-black text-green-700 text-lg">{completedCount}</div>
                <div className="text-xs font-bold text-green-600 uppercase tracking-wide">Completed</div>
              </div>
              <div className="text-center p-2 rounded-xl bg-red-50/80 border border-red-100 group-hover:bg-red-100/80 transition-colors duration-300">
                <div className="font-black text-red-700 text-lg">{lateCount}</div>
                <div className="text-xs font-bold text-red-600 uppercase tracking-wide">Late</div>
              </div>
              <div className="text-center p-2 rounded-xl bg-blue-50/80 border border-blue-100 group-hover:bg-blue-100/80 transition-colors duration-300">
                <div className="font-black text-blue-700 text-lg">{workshopCount - completedCount}</div>
                <div className="text-xs font-bold text-blue-600 uppercase tracking-wide">Active</div>
              </div>
            </div>
          </div>

          <div className="pt-3 space-y-2 border-t border-gray-200/70">
            <div className="flex justify-between items-center p-2 rounded-lg bg-orange-50/50 group-hover:bg-orange-100/50 transition-colors duration-300">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Estimated</span>
              <span className="font-black text-orange-700">{formatCurrency(estimatedSavings)}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg bg-green-50/50 group-hover:bg-green-100/50 transition-colors duration-300">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Released</span>
              <span className="font-black text-green-700">{formatCurrency(releasedSavings)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DepartmentDistribution: React.FC = () => {
  const { departmentStats, isLoading, error } = useDepartmentStats();
  const navigate = useNavigate();
  const { setFilters, fetchWorkshops } = useWorkshopStore();
  const { stats } = useDashboardStats();

  // Navigation handler for department cards
  // Preserves the startup date filter by NOT calling clearFilters()
  const handleDepartmentClick = async (departmentId: number) => {
    // Set department filter while preserving date filters, clear other filters
    setFilters({ department: departmentId, statuses: undefined, status: undefined, overdue: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center mb-6">
          <div className="w-6 h-6 bg-gray-200 rounded mr-3 animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-64 animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-6 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-8"></div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">Failed to load department statistics: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!departmentStats || departmentStats.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No department data</h3>
          <p className="mt-1 text-sm text-gray-500">No department statistics available.</p>
        </div>
      </div>
    );
  }

  // Filter out departments with 0 workshops and sort by workshop count (descending)
  const sortedDepartments = [...departmentStats]
    .filter(dept => dept.workshop_count > 0)
    .sort((a, b) => b.workshop_count - a.workshop_count);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-500 p-8 border border-gray-200/50 backdrop-blur-sm">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-400/10 via-transparent to-purple-400/10"></div>
        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-blue-400/10 animate-pulse"></div>
        <div className="absolute -bottom-20 -left-20 w-32 h-32 rounded-full bg-purple-400/10 animate-pulse delay-1000"></div>
      </div>
      
      <div className="relative z-10">
        <div className="flex items-center mb-8">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mr-4 shadow-xl">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Workshop Distribution by Department</h2>
        </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedDepartments.map((dept) => (
          <DepartmentCard
            key={dept.code}
            name={dept.name}
            code={dept.code}
            workshopCount={dept.workshop_count}
            completionRate={dept.completion_rate}
            completedCount={dept.completed}
            lateCount={dept.late}
            estimatedSavings={dept.estimated_savings}
            releasedSavings={dept.released_savings}
            color={dept.color}
            onClick={() => handleDepartmentClick(dept.id)}
          />
        ))}
      </div>
      
        {/* Summary Stats */}
        <div className="mt-10 pt-8 border-t border-gray-300/50">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="relative overflow-hidden text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200/50 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 transition-all duration-500 cursor-pointer group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="text-4xl font-black text-blue-800 group-hover:scale-110 transition-transform duration-300">
                  {sortedDepartments.length}
                </div>
                <div className="text-sm font-bold text-blue-700 uppercase tracking-wider mt-2">Active Departments</div>
              </div>
            </div>
            
            <div className="relative overflow-hidden text-center p-6 rounded-2xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200/50 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 transition-all duration-500 cursor-pointer group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="text-4xl font-black text-green-800 group-hover:scale-110 transition-transform duration-300">
                  {stats?.overview?.total_workshops || 0}
                </div>
                <div className="text-sm font-bold text-green-700 uppercase tracking-wider mt-2">Total Workshops</div>
              </div>
            </div>
            
            <div className="relative overflow-hidden text-center p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200/50 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 transition-all duration-500 cursor-pointer group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="text-4xl font-black text-purple-800 group-hover:scale-110 transition-transform duration-300">
                  {sortedDepartments.length > 0 
                    ? Math.round(sortedDepartments.reduce((sum, dept) => sum + dept.completion_rate, 0) / sortedDepartments.length)
                    : 0
                  }%
                </div>
                <div className="text-sm font-bold text-purple-700 uppercase tracking-wider mt-2">Average Completion</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDistribution;
